

function getCSSSelector(el) {
  try {
    if (el.id && !/^\d/.test(el.id)) {
      return `#${CSS.escape(el.id)}`;
    }
    for (const attr of ["data-testid", "data-test", "data-key", "name"]) {
      const val = el.getAttribute(attr);
      if (val) return `${el.tagName.toLowerCase()}[${attr}="${val}"]`;
    }
    if (el.tagName === "A") {
      const href = el.getAttribute("href") || "";
      const aria = el.getAttribute("aria-label") || "";
      if (href.startsWith("/")) return `a[href="${href}"]`;
      if (aria.trim()) return `a[aria-label="${aria.trim()}"]`;
    }
    if (el.tagName === "SELECT" && (el.name || el.id)) {
      return el.name ? `select[name="${el.name}"]` : `select#${CSS.escape(el.id)}`;
    }
    const stable = [...el.classList].filter(
      c => c.length > 2 && !/\d/.test(c) && !/active|selected|hover|focus/.test(c)
    ).slice(0, 2);
    if (stable.length) {
      const tag = el.tagName.toLowerCase();
      const cls = stable.map(c => `.${CSS.escape(c)}`).join("");
      const matches = document.querySelectorAll(`${tag}${cls}`);
      if (matches.length === 1) return `${tag}${cls}`;
    }
    return null;
  } catch (_) {
    return el.tagName.toLowerCase();
  }
}

function _buildPath(el) {
  const parts = [];
  let node = el;
  while (node && node !== document.body && parts.length < 5) {
    const tag = node.tagName.toLowerCase();
    const siblings = [...(node.parentElement?.children || [])].filter(s => s.tagName === node.tagName);
    const part = siblings.length > 1 ? `${tag}:nth-of-type(${siblings.indexOf(node) + 1})` : tag;
    parts.unshift(part);
    node = node.parentElement;
  }
  return parts.join(" > ");
}

function _cleanLabel(str) {
  return (str || "").replace(/[*✱✻†‡•]+/g, "").replace(/\s+/g, " ").trim();
}

function _getCustomSelectLabel(wrapper) {

  const formGroup =
    wrapper.closest(
      '[class*="col"], [class*="form"], [class*="field"], [class*="group"]'
    );

  if (formGroup) {

    const lbl =
      formGroup.querySelector(
        'label.select-floating-label, label'
      );

    if (lbl?.innerText?.trim()) {
      return _cleanLabel(
        lbl.innerText.trim()
      );
    }
  }

  const placeholder =
    wrapper.querySelector(
      '[class*="placeholder"]'
    )?.innerText?.trim();

  if (placeholder) {
    return _cleanLabel(
      placeholder
    );
  }

  return "";
}

function getDropdownLabel(el) {
  const aria = el.getAttribute("aria-label")?.trim();
  if (aria) return _cleanLabel(aria);
  let sib = el.previousElementSibling;
  while (sib) {
    if (sib.tagName === "LABEL") { const t = sib.innerText?.trim(); if (t) return _cleanLabel(t); }
    sib = sib.previousElementSibling;
  }
  const grp = el.closest('[class*="form"],[class*="field"],[class*="group"],[class*="col"]');
  if (grp) {
    const lbl = grp.querySelector("label");
    if (lbl) return _cleanLabel(lbl.innerText.trim());
  }
  return _cleanLabel(el.getAttribute("name") || el.id || "");
}

function extractMetrics(isVisibleFn) {
  const metrics = [];
  const seen = new Set();
  const statSelectors = [
    '[class*="stat"]','[class*="Stat"]','[class*="metric"]','[class*="Metric"]',
    '[class*="kpi"]','[class*="KPI"]','[class*="card"]','[class*="Card"]',
    '[class*="summary"]','[class*="Summary"]','[class*="widget"]','[class*="Widget"]',
    '[class*="count"]','[class*="Count"]','[class*="total"]','[class*="Total"]',
  ];
  for (const sel of statSelectors) {
    for (const el of document.querySelectorAll(sel)) {
      if (!isVisibleFn(el)) continue;
      const text = el.innerText?.replace(/\s+/g, " ").trim();
      if (!text || text.length < 2 || text.length > 300 || !/\d/.test(text)) continue;
      const key = text.slice(0, 60);
      if (seen.has(key)) continue;
      seen.add(key);
      metrics.push({ text, selector: getCSSSelector(el) });
      if (metrics.length >= 50) return metrics;
    }
  }
  for (const el of document.querySelectorAll("svg text")) {
    const t = el.textContent?.trim();
    if (!t || t.length > 50 || seen.has(t)) continue;
    seen.add(t);
    metrics.push({ text: t, source: "chart" });
    if (metrics.length >= 60) break;
  }
  return metrics;
}

export function extractDOM() {
  function isAgentUI(el) {
    return !!el.closest("[data-agent-ui]");
  }

  function isVisible(el) {
    if (!el) return false;
    if (isAgentUI(el)) return false;
    const s = window.getComputedStyle(el);
    if (s.display === "none" || s.visibility === "hidden" || s.opacity === "0") return false;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function labelFromHref(href) {
    if (!href || !href.startsWith("/")) return "";
    const parts = href.split("/").filter(Boolean);
    if (parts.length === 0) return "Dashboard";
    let seg = parts[0];
    let expanded = "";
    for (let i = 0; i < seg.length; i++) {
      const code = seg.charCodeAt(i);
      if (i > 0 && code >= 65 && code <= 90) expanded += " ";
      expanded += seg[i];
    }
    return expanded.replace(/-/g, " ").replace(/_/g, " ")
      .split(" ")
      .filter(w => w.length > 0)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  }

  function getDirectText(el) {
    let direct = "";
    for (const node of el.childNodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        direct += node.textContent;
      }
    }
    direct = direct.replace(/\s+/g, " ").trim();
    if (direct) return direct;

    for (const child of el.children) {
      const tag = child.tagName.toLowerCase();
      if (tag === "span" || tag === "p" || tag === "strong" || tag === "em") {
        if (child.children.length === 0) {
          const t = child.textContent?.replace(/\s+/g, " ").trim();
          if (t) return t;
        }
      }
    }
    return "";
  }

  function getText(el) {
    const ariaLabel = el.getAttribute("aria-label")?.trim();
    if (ariaLabel) return ariaLabel;
    const title = el.getAttribute("title")?.trim();
    if (title) return title;
    const dataName = el.getAttribute("data-name")?.trim();
    if (dataName) return dataName;

    const isNavEl = !!el.closest(
      "nav, [class*='sidebar'], [class*='Sidebar'], [class*='navbar'], [class*='Navbar'], [class*='menu']"
    );
    if (isNavEl) {
      const direct = getDirectText(el);
      if (direct && direct.length < 80) return direct;

      if (el.tagName === "A") {
        const href = el.getAttribute("href") || "";
        return labelFromHref(href);
      }
      return direct;
    }

    const raw =
      (
        el.innerText ||
        el.textContent ||
        el.getAttribute("data-ai-agent") ||
        ""
      )
      .replace(/\s+/g, " ")
      .trim();

    if (raw && raw.length < 80) return raw;
    if (el.tagName === "A") {
      const href = el.getAttribute("href") || "";
      return labelFromHref(href);
    }
    return "";
  }

  const elements = [
    ...document.querySelectorAll(`
      a,
      button,
      [role='button'],
      [role='tab'],
      [role='menuitem'],
      [role='option'],
      .react-select__control,
      [class$='-control'],
      .link-text,
      [class*='link'],
      [class*='report'],
      [data-ai-agent]
    `)
  ];



  let _uidCounter = 0;
  const elToItem  = new WeakMap();  
  const clickable = [];             

  const seenNav  = new Set();  
  const seenPage = new Set();   

  for (const el of elements) {
    if (isAgentUI(el)) continue;

    const isInNav = !!el.closest(
      "nav, [class*='sidebar'], [class*='Sidebar'], [class*='navbar'], [class*='Navbar'], [class*='menu']"
    );
    

    const isDropdown =
      [...(el.classList || [])].some(
        c =>
          c === "react-select__control" ||
          c.endsWith("-control")
      );

    if (!isDropdown && !isInNav && !isVisible(el)) continue;

    const isHeaderToggle =
      isInNav &&
      el.tagName === "A" &&
      !el.getAttribute("href") &&
      el.classList?.contains("dropdown-indicator");
    if (isHeaderToggle && !isVisible(el)) continue;

    const text    = getText(el);
    const aiAgent = el.getAttribute("data-ai-agent") || "";
    if ((!text && !aiAgent) || text.length > 120) continue;

    let href = "";
    if (el.tagName === "A") {
      href = el.getAttribute("href") || "";
    } else {
      const parentLink = el.closest("a");
      if (parentLink) href = parentLink.getAttribute("href") || "";
    }

    const textKey =
      text.toLowerCase() + "|" + href;
    if (isInNav) {
      if (seenNav.has(textKey)) continue;
      seenNav.add(textKey);
    } else {
      const pageKey = textKey + "|" + href + "|" + (el.getBoundingClientRect().top || 0);
      if (seenPage.has(pageKey)) continue;
      seenPage.add(pageKey);
    }

    if (text === "US" && href?.includes("gizmott.com")) continue;

    let selector = isDropdown
      ? (getCSSSelector(el) || _buildPath(el))
      : getCSSSelector(el);

    if (!selector) {
      selector = "";
    }

    const node = {
      text:     isDropdown ? `[Dropdown] ${getDropdownLabel(el) || text}` : text,
      tag:      el.tagName.toLowerCase(),
      id:       el.id    || "",
      class:    el.className || "",
      selector,
      isNav:    isInNav,
      href,
      aiAgent,
      children: [],
      _el:      el,
    };

    elToItem.set(el, node);
    clickable.push(node);
  }

  const hierarchical = [];
  const addedToParent = new Set(); 

  for (const node of clickable) {
    const el = node._el;
    if (!el) { hierarchical.push(node); continue; }

    if (!node.isNav) {
      hierarchical.push(node);
      continue;
    }

    const currentLi  = el.closest("li");
    const parentLi   = currentLi?.parentElement?.closest("li");

    if (!parentLi) {
      hierarchical.push(node);
      continue;
    }
    const parentAnchor = Array.from(parentLi.children).find(c => c.tagName === "A");

    if (parentAnchor && parentAnchor !== el) {
      const parentNode = elToItem.get(parentAnchor);

      if (parentNode && parentNode !== node) {

        if (!parentNode.children.some(c => c === node)) {
          parentNode.children.push(node);
        }
        addedToParent.add(node);
        continue;
      }
    }

    hierarchical.push(node);
  }

  for (const node of clickable) {
    delete node._el;
  }

  function getInputLabel(el) {
    if (el.id) {
      const lbl = document.querySelector(`label[for="${el.id}"]`);
      if (lbl?.innerText?.trim()) return lbl.innerText.trim();
    }
    const wrapping = el.closest("label");
    if (wrapping?.innerText?.trim()) return wrapping.innerText.trim();
    const group = el.closest(
      "[class*='form'],[class*='field'],[class*='group'],[class*='input']"
    );
    if (group) {
      const lbl = group.querySelector("label");
      if (lbl?.innerText?.trim()) return lbl.innerText.trim();
    }
    const td = el.closest("td, th");
    if (td) {
      const tr    = td.parentElement;
      const table = tr?.closest("table");
      if (table) {
        const colIdx = [...(tr?.children || [])].indexOf(td);
        const th = table.querySelector(
          `thead tr th:nth-child(${colIdx + 1}), thead tr td:nth-child(${colIdx + 1})`
        );
        if (th?.innerText?.trim()) return _cleanLabel(th.innerText.trim());
      }
    }
    const parent = el.parentElement;
    if (parent) {
      for (const sib of parent.children) {
        if (sib === el) continue;
        if (sib.querySelector("input,select,textarea")) continue;
        const t = sib.innerText?.trim();
        if (t && t.length > 1 && t.length < 80) return _cleanLabel(t);
      }
    }
    let prev = el.previousElementSibling;
    for (let i = 0; i < 3 && prev; i++) {
      const pt = prev.innerText?.trim();
      if (pt && pt.length > 1 && pt.length < 80) return _cleanLabel(pt);
      prev = prev.previousElementSibling;
    }
    return (
      el.getAttribute("aria-label") ||
      el.placeholder ||
      el.name ||
      el.id ||
      _deriveContextLabel(el) ||
      ""
    ).trim();
  }

  function _deriveContextLabel(el) {
    const card = el.closest(
      '[class*="card"],[class*="Card"],[class*="field"],[class*="Field"],[class*="section"],form,tr,li'
    );
    if (card) {
      const h = card.querySelector(
        "h1,h2,h3,h4,h5,h6,[class*='title'],[class*='Title'],[class*='heading'],[class*='Heading']"
      );
      if (h?.innerText?.trim()) return h.innerText.trim().slice(0, 60);
      const lbl = card.querySelector("label,[class*='label'],[class*='Label']");
      if (lbl?.innerText?.trim()) return lbl.innerText.trim().slice(0, 60);
    }
    let prev = el.previousElementSibling;
    for (let i = 0; i < 3 && prev; i++) {
      const t = prev.innerText?.trim();
      if (t && t.length > 1 && t.length < 60) return t;
      prev = prev.previousElementSibling;
    }
    return (
      el.parentElement?.getAttribute("aria-label") ||
      el.parentElement?.getAttribute("data-label") ||
      el.parentElement?.getAttribute("title") ||
      ""
    ).trim().slice(0, 60);
  }

  const selects = [...document.querySelectorAll("select")]
    .filter(isVisible)
    .map(el => ({
      label:    getDropdownLabel(el),
      name:     el.name    || "",
      id:       el.id      || "",
      value:    el.value   || "",
      selector: getCSSSelector(el),
      options:  [...el.options].map(o => ({ text: o.text.trim(), value: o.value, selected: o.selected })),
      aiAgent:  el.getAttribute("data-ai-agent") || "",
    }));

  const customDropdowns = [];
  const ddSeen = new Set();

  for (const wrapper of document.querySelectorAll(
    '.react-select__control, [class$="-control"]'
  )) {
    if (!isVisible(wrapper)) continue;
    let label = _getCustomSelectLabel(wrapper);

    if (!label) {
      label =
        wrapper
          .querySelector('.react-select__placeholder, [class*="placeholder"]')
          ?.innerText
          ?.trim() || "";

      if (!label) {
        const hiddenInput =
          wrapper.querySelector('input[type="hidden"]');

        const containerId =
          wrapper.querySelector(".react-select-container")?.id || "";

        const raw =
          hiddenInput?.name || containerId;

        if (raw) {
          label = raw
            .replace(/_/g, " ")
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .replace(/\b\w/g, c => c.toUpperCase())
            .trim();
        }
      }
    }

    console.log(
      "DROPDOWN DETECTED",
      wrapper,
      "label:",
      label
    );
    if (!label) continue;
    const labelKey = label.toLowerCase();
    if (ddSeen.has(labelKey)) continue;
    ddSeen.add(labelKey);

    const multiVals = [...wrapper.querySelectorAll(".react-select__multi-value__label")]
      .map(v => v.innerText?.trim()).filter(Boolean);
    const singleVal =
      wrapper.querySelector(
        '.react-select__single-value, [class*="single-value"]'
      );

    const currentValue =
      multiVals.length
        ? multiVals.join(", ")
        : (
            singleVal?.innerText?.trim()
            || ""
          );

    const cleanedValue =
      currentValue
        .replace(/option .* selected\.?/gi, "")
        .replace(/\d+\s+result[s]?\s+available\.?/gi, "")
        .replace(/focused\.?/gi, "")
        .trim();

    const menu =
      document.querySelector(
        '.react-select__menu, [role="listbox"]'
      );

    const options =
      menu
        ? [...menu.querySelectorAll('[role="option"]')]
            .map(o => o.innerText?.trim())
            .filter(Boolean)
        : [];
    
    const container = wrapper.querySelector(".react-select-container");
    const containerId = container?.id;
    const selector =
      getCSSSelector(wrapper) ||
      _buildPath(wrapper);

    customDropdowns.push({
      label,
      selector,
      currentValue: cleanedValue,
      isOpen:   !!wrapper.querySelector('.react-select__menu, [role="listbox"]'),
      options,
      aiAgent:  wrapper.getAttribute("data-ai-agent") || "",
    });
  }

  const inputs = [...document.querySelectorAll("input, textarea")]
    .filter(isVisible)
    .map(el => {

      let selector = getCSSSelector(el);
      const stableInputSelector =
        el.getAttribute("name") ||
        el.getAttribute("placeholder") ||
        el.getAttribute("aria-label") ||
        el.id;

      if (!selector && stableInputSelector) {

        if (el.getAttribute("name")) {
          selector =
            `${el.tagName.toLowerCase()}[name="${el.getAttribute("name")}"]`;

        } else if (el.getAttribute("placeholder")) {
          selector =
            `${el.tagName.toLowerCase()}[placeholder="${el.getAttribute("placeholder")}"]`;

        } else if (el.getAttribute("aria-label")) {
          selector =
            `${el.tagName.toLowerCase()}[aria-label="${el.getAttribute("aria-label")}"]`;

        } else if (el.id) {
          selector =
            `#${CSS.escape(el.id)}`;
        }
      }
      if (!selector) {
        selector = _buildPath(el);
      }

      return {
        placeholder: el.placeholder || "",
        name:        el.name || "",
        id:          el.id || "",
        type:        el.type || el.tagName.toLowerCase(),
        value:       el.value || "",
        label:       getInputLabel(el),

        selector,

        context:     _deriveContextLabel(el) || "",
        aiAgent:     el.getAttribute("data-ai-agent") || "",
      };
    });

  const fileInputs = [...document.querySelectorAll('input[type="file"]')]
    .filter(el => !isAgentUI(el))
    .map(el => ({
      selector: getCSSSelector(el),
      accept:   el.accept   || "*",
      multiple: el.multiple,
      name:     el.name     || "",
      id:       el.id       || "",
      label:    getInputLabel(el) || _deriveContextLabel(el),
    }));

  const toggles = [...document.querySelectorAll(
    'input[type="checkbox"], input[type="radio"], [role="switch"], [role="checkbox"], [role="radio"]'
  )].filter(el => !isAgentUI(el) && (isVisible(el) || !!el.closest("label")))
    .map(el => ({
      selector: getCSSSelector(el),
      type:     el.type || el.getAttribute("role") || "checkbox",
      checked:  el.checked ?? (el.getAttribute("aria-checked") === "true"),
      disabled: el.disabled || el.getAttribute("aria-disabled") === "true",
      label:    getInputLabel(el) || _deriveContextLabel(el) || el.name || el.id || "",
      name:     el.name || "",
    }));

  const images = [...document.querySelectorAll(
    'img, [class*="thumbnail"], [class*="Thumbnail"], [class*="cover"], [class*="Cover"], ' +
    '[class*="poster"], [class*="Poster"], [class*="banner"], [class*="Banner"], [class*="thumb"]'
  )].filter(el => !isAgentUI(el) && isVisible(el))
    .slice(0, 20)
    .map(el => {
      const isImg = el.tagName === "IMG";
      const src   = isImg ? (el.src || el.getAttribute("data-src") || "") : "";
      const isEmpty = !src
        || src.includes("placeholder") || src.includes("no-image")
        || src.includes("default-thumb") || src.includes("blank")
        || (isImg && el.naturalWidth === 0 && el.complete);
      return {
        selector: getCSSSelector(el),
        alt:      isImg ? (el.alt || "") : "",
        src:      src.slice(0, 80),
        isEmpty,
        label:    _deriveContextLabel(el) || (isImg ? el.alt : "") || "",
      };
    });

  const metrics = extractMetrics(isVisible);

  const modalOpen = !![
    ...document.querySelectorAll(
      "[role='dialog'], [class*='modal'], [class*='Modal'], [class*='drawer'], [class*='Drawer']"
    )
  ].find(el => isVisible(el));

  const activeNavEl = document.querySelector(
    "nav .active, nav [class*='active'], [class*='sidebar'] .active, [class*='sidebar'] [aria-current='page']"
  );
  const activeNav = activeNavEl?.innerText?.trim() || activeNavEl?.getAttribute("aria-label") || "";

  const tables = [];
  document.querySelectorAll("table").forEach(tbl => {
    if (!isVisible(tbl)) return;
    const headers = [...tbl.querySelectorAll("thead th, thead td")]
      .map(th => th.innerText?.trim()).filter(Boolean);
    const rows = [...tbl.querySelectorAll("tbody tr")].map(tr =>
      [...tr.querySelectorAll("td")].map(td => td.innerText?.replace(/\s+/g, " ").trim()).filter(Boolean)
    );
    if (headers.length || rows.length) tables.push({ headers, rows });
  });

  const pageText = (document.body?.innerText || "").replace(/\s+/g, " ").slice(0, 8000);

  return {
    clickable: hierarchical,
    selects,
    customDropdowns,
    inputs,
    fileInputs,
    toggles,
    images,
    metrics,
    modalOpen,
    tables,
    page: {
      title:     document.title,
      path:      window.location.pathname,
      activeNav,
      url:       window.location.href,
      pageText,
    }
  };
}
export function serializeForLLM(dom) {
  const lines = [];
  const page  = dom.page || {};

  lines.push(`## PAGE`);
  lines.push(`URL: ${page.url || page.path || "/"}`);
  lines.push(`Title: ${page.title || ""}`);
  if (page.activeNav) lines.push(`Active section: ${page.activeNav}`);

  const rendered = new Set();

  function renderClickableTree(nodes, depth = 0) {
    for (const node of nodes || []) {
      const indent = "  ".repeat(depth);
      const kind   = node.isNav ? "NAV" : "ACTION";
      const renderKey =
        node.selector ||
        `${node.text}-${node.isNav}`;

      if (rendered.has(renderKey)) continue;
      rendered.add(renderKey);
      lines.push(
        `${indent}[${kind}] ${node.text} selector="${node.selector}" ai_agent="${node.aiAgent || ""}"`
      );
      if (node.children && node.children.length) {
        renderClickableTree(node.children, depth + 1);
      }
    }
  }

  if ((dom.clickable || []).length) {
    lines.push(`\n## CLICKABLE TREE`);
    renderClickableTree(dom.clickable);
  }

  if ((dom.selects || []).length) {
    lines.push(`\n## DROPDOWN SELECTS (${dom.selects.length} dropdowns)`);
    for (const s of dom.selects) {
      const label = s.label || s.name || s.id || "dropdown";
      const opts  = (s.options || []).map(o => o.text).join(" | ");
      lines.push(`  [${label}]  type="select"  selector="${s.selector}" ai_agent="${s.aiAgent || ""}" value="${s.value}"  options="${opts}"`);
    }
  }

  if ((dom.customDropdowns || []).length) {
    lines.push(`\n## CUSTOM DROPDOWNS (${dom.customDropdowns.length} dropdowns)`);
    for (const d of dom.customDropdowns) {
      const opts = d.options?.length ? `  options="${d.options.join(" | ")}"` : "";
      lines.push(`  [${d.label}]  selector="${d.selector}" ai_agent="${d.aiAgent || ""}"  current="${d.currentValue}"${opts}`);
    }
  }

  if ((dom.inputs || []).length) {
    lines.push(`\n## FORM INPUTS`);
    for (const inp of dom.inputs) {
      const label = inp.label || inp.placeholder || inp.name || inp.id || "field";
      lines.push(`  [${label}]  type="${inp.type}"  selector="${inp.selector}" ai_agent="${inp.aiAgent || ""}" value="${inp.value}"`);
    }
  }

  if ((dom.fileInputs || []).length) {
    lines.push(`\n## FILE UPLOAD INPUTS (${dom.fileInputs.length} inputs)`);
    for (const f of dom.fileInputs) {
      const label = f.label || f.name || f.id || "file";
      lines.push(`  [${label}]  selector="${f.selector}" ai_agent="${f.aiAgent || ""}"  accept="${f.accept}"  multiple=${f.multiple}`);
    }
  }

  if ((dom.toggles || []).length) {
    lines.push(`\n## TOGGLES & CHECKBOXES (${dom.toggles.length} items)`);
    for (const t of dom.toggles) {
      const state = t.checked ? "ON ✓" : "OFF";
      const dis   = t.disabled ? " [DISABLED]" : "";
      lines.push(`  [${t.label || t.name || "toggle"}]  type="${t.type}"  selector="${t.selector}" ai_agent="${t.aiAgent || ""}"  state=${state}${dis}`);
    }
  }

  const emptyImgs  = (dom.images || []).filter(i => i.isEmpty);
  const filledImgs = (dom.images || []).filter(i => !i.isEmpty);
  if (emptyImgs.length) {
    lines.push(`\n## MISSING / EMPTY IMAGES (${emptyImgs.length} — need upload)`);
    for (const img of emptyImgs) {
      lines.push(`  [${img.label || img.alt || "image"}]  selector="${img.selector}"  status=EMPTY  needs_upload=true`);
    }
  }
  if (filledImgs.length) {
    lines.push(`\n## PAGE IMAGES (${filledImgs.length} loaded)`);
    for (const img of filledImgs) {
      lines.push(`  [${img.label || img.alt || "image"}]  selector="${img.selector}"`);
    }
  }

  if ((dom.tables || []).length) {
    lines.push(`\n## DATA TABLES`);
    for (const t of dom.tables) {
      if (t.headers.length) lines.push(`  Headers: ${t.headers.join(" | ")}`);
      t.rows.slice(0, 10).forEach((row, i) => lines.push(`  Row ${i + 1}: ${row.join(" | ")}`));
    }
  }

  if ((dom.metrics || []).length) {
    lines.push(`\n## PAGE METRICS & STATS`);
    for (const m of dom.metrics) {
      lines.push(`  ${m.text}`);
    }
  }

  if (dom.modalOpen) lines.push(`\n## MODAL: A dialog/modal is currently open on screen.`);

  return lines.join("\n");
}